/**
 * @packageDocumentation
 * @module StockChartShared
 */
import { Injectable } from '@angular/core';
import { isNullOrUndefined, isNull } from 'util';
import { ToastrService } from 'ngx-toastr';
import { isEmptyField } from './stock-chart-util.service';

@Injectable({
  providedIn: 'root'
})
export class StockChartDataService {
  ToastrConstant = {
    closeButton: true,
    timeOut: 3000,
    extendedTimeOut: 2000
  };

  constructor(
    private toastr: ToastrService
  ) { }

  showToastMessage(message: string, type: 'error' | 'success' | 'warning', options?: any): void {
    let toastOptions: any = this.ToastrConstant;
    if (!isNullOrUndefined(options)) {
      toastOptions = { ...this.ToastrConstant, ...options.options };
    }
    this.toastr.clear();
    this.toastr[type](message, toastOptions.title, toastOptions);
  }

  deepMerge(src: any, dest: any, options?: any): any {
    const result = {};
    // tslint:disable-next-line: forin
    for (const i in src) {
      const type = typeof src[i];
      if (!isNullOrUndefined(options) && !isNullOrUndefined(options.customMerge) && !isNullOrUndefined(options.customMerge[i])) {

        // tslint:disable-next-line: triple-equals
        if (options.customMerge[i] == 'dest') {
          result[i] = !isNullOrUndefined(dest[i]) ? dest[i] : null;
          // tslint:disable-next-line: triple-equals
        } else if (options.customMerge[i] == 'src') {
          result[i] = !isNullOrUndefined(src[i]) ? src[i] : null;
        } else {
          result[i] = options.customMerge[i](src[i], dest[i]);
        }
      } else if (!isNullOrUndefined(src) && !isNull(src[i]) && type == 'object' && !(src[i] instanceof Array)) {
        result[i] = (!isNullOrUndefined(dest) && !isNullOrUndefined(dest[i])) ? this.deepMerge(src[i], dest[i], options) : src[i];
      } else if (!isNullOrUndefined(src) && !isNullOrUndefined(src[i]) && src[i] instanceof Array) {
        if (!isNullOrUndefined(options) && !isNullOrUndefined(options.array) && options.array == 'merge') {
          result[i] = (!isNullOrUndefined(dest) && !isNullOrUndefined(dest[i]) && dest[i].length > 0) ? src[i].concat(dest[i]) : src[i];
        } else if (!isNullOrUndefined(options) && !isNullOrUndefined(options.array) && options.array == 'override') {
          result[i] = (!isNullOrUndefined(dest) && !isNullOrUndefined(dest[i])) ? dest[i] : src[i];
        } else {
          result[i] = (!isNullOrUndefined(dest) && !isNullOrUndefined(dest[i]) && dest[i].length > 0) ? src[i].concat(dest[i]) : src[i];
        }
      } else {
        result[i] = (!isNullOrUndefined(dest) && !isNullOrUndefined(dest[i])) ? dest[i] : src[i];
      }
    }
    for (const i in dest) {
      if (isNullOrUndefined(result[i])) {
        result[i] = dest[i];
      }
    }
    return result;
  }

  deepClone(object): any {
    if (Array.isArray(object)) {
      return JSON.parse(JSON.stringify(object));
    }
    const cloneObj = ( object.constructor() as any);
    const attributes = Object.keys(object);
    for (const attribute of attributes) {
      const property = object[attribute];

      if (typeof property === 'object') {
        cloneObj[attribute] = this.deepClone(property);
      } else {
        cloneObj[attribute] = property;
      }
    }
    return cloneObj;
  }

  flattenObject(data) {
    return data.map((value, key) => this.flattenOneObject(value));
  }

  flattenOneObject(ob) {
    if (typeof ob === 'string') {
      return ob;
    }
    const toReturn = {};
    for (const i in ob) {
      if (!ob.hasOwnProperty(i)) { continue; }
      // tslint:disable-next-line: triple-equals
      if ((typeof ob[i]) == 'object' && !(ob[i] instanceof Array)) {
        const flatObject = this.flattenOneObject(ob[i]);
        for (const x in flatObject) {
          if (!flatObject.hasOwnProperty(x)) { continue; }
          toReturn[i + '.' + x] = flatObject[x];
        }
      } else {
        toReturn[i] = ob[i];
      }
    }
    return toReturn;
  }

  deflattenObject(obj: any) {
    const type = typeof obj;
    const result = {};
    if (type == 'object' && !(obj instanceof Array)) {
      // tslint:disable-next-line: forin
      for (const i in obj) {
        const allKeys = i.split('.');
        // tslint:disable-next-line: prefer-const
        // tslint:disable-next-line: one-variable-per-declaration
        let temp = result, length = allKeys.length;
        const lastKey = allKeys[length - 1];
        allKeys.forEach((key, i) => {
          if (i < (length - 1)) {
            temp[key] = temp[key] || {};
            temp = temp[key];
          }
        });
        temp[lastKey] = obj[i];
      }
      return result;
    } else {
      return obj;
    }
  }


  clearEmptyFields(data, emptyFillWith?) {
    if (isEmptyField(emptyFillWith)) {
      emptyFillWith = null;
    }
    let returnableObject: any = {};
    let flattenData: any;
    let isObjectEmpty = true;
    if (Array.isArray(data)) {
      flattenData = this.flattenObject(data);
      flattenData.forEach( singleObject => {
        [singleObject, isObjectEmpty] = this.clearOneObjectEmptyFields(singleObject, isObjectEmpty, emptyFillWith);
      });
    } else {
      flattenData = this.flattenOneObject(data);
      [flattenData, isObjectEmpty] = this.clearOneObjectEmptyFields(flattenData, isObjectEmpty, emptyFillWith);
    }
    const deflattenObject = this.deflattenObject(flattenData);
    returnableObject.data = deflattenObject;
    returnableObject.isObjectEmpty = isObjectEmpty;
    return deflattenObject;
  }

  clearOneObjectEmptyFields(flattenData, isObjectEmpty, emptyFillWith) {
    for (const val of Object.keys(flattenData)) {
      if (isNullOrUndefined(flattenData[val]) || flattenData[val] === '') {
        flattenData[val] = emptyFillWith;
      }
    }
    return [flattenData, isObjectEmpty];
  }

}
